/**
 * Types and constants shared by the server and the browser client.
 * Everything that crosses the wire is defined here.
 */

export type Role = 'host' | 'player';

export interface TeamView {
  id: string;
  name: string;
  score: number;
  color: string;
}

export interface ParticipantView {
  id: string;
  name: string;
  role: Role;
  teamId: string | null;
  /** At least one browser tab is connected to the state channel. */
  online: boolean;
  /** Player has whisper mode on (held or toggled). Shown to everyone as "huddling". */
  whispering: boolean;
}

export interface QuizView {
  code: string;
  name: string;
  hostId: string;
  teams: TeamView[];
  participants: ParticipantView[];
  /** Quizmaster is holding/toggled Announcement. */
  announcing: boolean;
  /** Increments on every change; lets clients ignore stale snapshots. */
  version: number;
}

// ---- State channel (WebSocket) ----

export type ClientMessage =
  | { type: 'ping' }
  | { type: 'whisper'; active: boolean }
  // host only
  | { type: 'announce'; active: boolean }
  | { type: 'assignTeam'; participantId: string; teamId: string | null }
  | { type: 'addScore'; teamId: string; delta: number }
  | { type: 'setScore'; teamId: string; score: number }
  | { type: 'renameTeam'; teamId: string; name: string }
  | { type: 'addTeam' }
  | { type: 'removeTeam'; teamId: string }
  | { type: 'renameQuiz'; name: string }
  | { type: 'mute'; participantId: string }
  | { type: 'kick'; participantId: string };

export type ServerMessage =
  | { type: 'state'; quiz: QuizView }
  | { type: 'pong' }
  | { type: 'forceMute' }
  | { type: 'kicked' }
  | { type: 'error'; message: string };

// ---- HTTP API ----

export interface Credentials {
  code: string;
  participantId: string;
  secret: string;
}

export interface CreateQuizRequest {
  quizName: string;
  hostName: string;
}

export interface CreateQuizResponse extends Credentials {
  hostKey: string;
}

export interface JoinRequest {
  name: string;
}

export interface TokenResponse {
  livekitUrl: string;
  main: { room: string; token: string };
  /** Present only for players who are on a team. Never issued to the quizmaster. */
  team: { room: string; token: string; teamId: string } | null;
}

// ---- LiveKit naming ----

export const roomNames = {
  main: (code: string) => `elixir-${code}`,
  team: (code: string, teamId: string) => `elixir-${code}--${teamId}`,
};

/** Names of the audio tracks each participant publishes. */
export const TRACK = {
  /** Main room: everyone's normal public voice. */
  publicMic: 'mic-public',
  /** Main room, quizmaster only: priority announcement voice. */
  announce: 'mic-announce',
  /** Team room: whisper voice. */
  teamMic: 'mic-team',
} as const;

export const TEAM_COLORS = [
  '#FF5E7A', // raspberry
  '#2FC4B2', // teal
  '#F5B82E', // marigold
  '#8F7CFF', // iris
  '#5BA8FF', // sky
  '#B6E05A', // lime
  '#FF9F45', // tangerine
  '#E07BE0', // orchid
];

export const LIMITS = {
  maxTeams: 8,
  minTeams: 1,
  maxParticipants: 40,
  nameLength: 32,
  quizNameLength: 60,
  teamNameLength: 24,
};

/** Quiz codes avoid look-alike characters (0/O, 1/I/L). */
export const CODE_ALPHABET = 'ABCDEFGHJKMNPQRSTUVWXYZ23456789';
export const CODE_LENGTH = 6;
